// @givmo/app-admin — Steps Competition CREATE + EDIT-DRAFT form (EP-WEB10).
//
// One component serving both routes because they are the same form minus one field:
//   - `/competitions/new`             → `POST /admin/company/steps-competitions` (creates a DRAFT).
//   - `/competitions/{id}/edit`       → `PUT  /admin/company/steps-competitions/{id}` (edits a DRAFT).
//
// ★ THE PRIZE IS IMMUTABLE AFTER CREATE. It is set here on the create path only; the backend's
// `UpdateBody` has no prize field at all (it is locked the moment the publish hold is placed), so the
// edit path renders the prize READ-ONLY rather than a control that would silently do nothing. The $10
// floor (SC-FU3) is mirrored as client validation with a live hint — the backend 422
// `PRIZE_BELOW_MINIMUM` remains the truth and is surfaced verbatim if the two ever disagree.
//
// Only a DRAFT is editable. Arriving at the edit route for a non-DRAFT competition shows an explanation
// and a way back rather than a form whose save would 409 — the state is knowable before the click.
//
// ★ THE SPONSOR DPA CLICK-THROUGH (SC-GO2) lives on the CREATE path only: creating a competition is
// where a company binds itself as Givfi's processor of participant health data. The gate never wedges
// the admin — only `required` (a readable Addendum, unticked) disables Create; a state that could not
// be read leaves Create enabled and lets the SERVER decide, recovering through its own 422. See
// `sponsor-dpa-section.tsx` for the three rails.
//
// DARK: reached only via the `/competitions/*` routes, which redirect to /dashboard while
// `VITE_COMPANY_STEPS_COMPETITIONS_ENABLED` is off.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  stepsCompetitionsAdminCreateAdminStepsCompetitionMutation,
  stepsCompetitionsAdminGetAdminStepsCompetitionOptions,
  stepsCompetitionsAdminGetAdminStepsCompetitionQueryKey,
  stepsCompetitionsAdminUpdateAdminStepsCompetitionMutation,
} from '@givmo/api-client/query';
import { givmoClient } from '@givmo/api-client';
import type { AdminStepsCompetitionOut, StepsCompetitionVisibility } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DatePicker,
  Field,
  Icon,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  testIdProp,
  toast,
  type SelectOption,
} from '@givmo/design-system';
import { ArrowLeft } from 'lucide-react';
import { isCompanyStepsCompetitionsEnabled } from '../../lib/env';
import { useEmployerModulePaths } from '../../lib/module-paths';
import {
  DEGENERATE_RULE_COPY,
  DESCRIPTION_MAX_LENGTH,
  MIN_PRIZE_HINT,
  NAME_MAX_LENGTH,
  competitionStatusView,
  errorMessage,
  extractSponsorDpa,
  formatPrize,
  isDraft,
  isSponsorDpaAcceptanceRequired,
  isoDayToPickedDay,
  parsePrizeDollars,
  pickedDayToIsoDay,
  prizeIsBelowMinimum,
  resolveSponsorDpaGate,
  sponsorDpaFromError,
  validateDateRange,
  type CreateCompetitionBody,
  type SponsorDpaGate,
  type SponsorDpaState,
} from './competitions-decode';
import { SponsorDpaSection } from './sponsor-dpa-section';

const PREFIX = 'competitions-form';

/**
 * The Sponsor DPA state read. Not in the committed OpenAPI snapshot (it landed after the last
 * regen), so it goes through the configured client directly — same interceptor stack (Bearer, App
 * Check, correlation, `GivmoError` mapping) every generated op uses.
 */
const SPONSOR_DPA_URL = '/admin/company/steps-competitions/sponsor-dpa';
const SPONSOR_DPA_QUERY_KEY = ['steps_competitions_admin-sponsor_dpa'];

/** Defense in depth behind the disabled Create button — never reached by a click on it. */
const SPONSOR_DPA_BLOCKED_COPY = 'Accept the Data Processing Addendum to create a competition.';

/** The 422 recovery, when the refusal handed back an Addendum we can now put on screen. */
export const SPONSOR_DPA_RECOVERY_COPY =
  'Review and accept the Data Processing Addendum below, then create the competition.';

/**
 * Type-level default for a refusal that carried no readable Addendum. The backend's handler always
 * sets a title AND a detail on this 422, so in practice the server's own copy is what renders; this
 * exists so the branch cannot render empty, and deliberately does not instruct the admin to tick a
 * box that — in this state — is not on screen.
 */
const SPONSOR_DPA_NO_DOCUMENT_COPY =
  'Your company must accept the Data Processing Addendum before creating a competition.';

/** The IANA competition timezone drives day-boundary evaluation (§2.3). Default ET, matching iOS. */
const DEFAULT_TIMEZONE = 'America/New_York';

const TIMEZONE_OPTIONS: SelectOption[] = [
  { value: 'America/New_York', label: 'Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'Central (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain (America/Denver)' },
  { value: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Pacific/Honolulu)' },
];

const VISIBILITY_OPTIONS: SelectOption[] = [
  { value: 'EMPLOYEES_ONLY', label: 'Employees only' },
  { value: 'PUBLIC', label: 'Public' },
];

export interface CompetitionFormPageProps {
  /** `null` on the create route; the competition id on the edit route. */
  competitionId: string | null;
}

export function CompetitionFormPage({ competitionId }: CompetitionFormPageProps) {
  const navigate = useNavigate();
  const paths = useEmployerModulePaths();
  const enabled = isCompanyStepsCompetitionsEnabled();

  useEffect(() => {
    if (!enabled) void navigate({ to: paths.dashboard });
  }, [enabled, navigate, paths.dashboard]);
  if (!enabled) return null;

  return competitionId === null ? (
    <CompetitionForm competition={null} />
  ) : (
    <EditDraftLoader competitionId={competitionId} />
  );
}

/** The edit path loads the DRAFT first — the form is seeded from the wire, never from stale state. */
function EditDraftLoader({ competitionId }: { competitionId: string }) {
  const navigate = useNavigate();
  const paths = useEmployerModulePaths();
  const detail = useQuery(
    stepsCompetitionsAdminGetAdminStepsCompetitionOptions({
      path: { steps_competition_id: competitionId },
    }),
  );

  if (detail.isPending) {
    return (
      <FormShell title="Edit competition">
        <div className="flex flex-col gap-12" {...testIdProp(`${PREFIX}-loading`)}>
          <Skeleton className="h-[120px] w-full rounded-lg" />
          <Skeleton className="h-[220px] w-full rounded-lg" />
        </div>
      </FormShell>
    );
  }

  if (detail.isError) {
    return (
      <FormShell title="Edit competition">
        <Card {...testIdProp(`${PREFIX}-error`)}>
          <CardBody className="flex flex-col items-center gap-8 py-24 text-center">
            <p className="text-s14 font-w500 text-danger-700">
              {errorMessage(detail.error, 'Failed to load this competition.')}
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => void detail.refetch()}
              testId={`${PREFIX}-retry`}
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      </FormShell>
    );
  }

  const competition = detail.data as AdminStepsCompetitionOut;

  // Only a DRAFT is editable. Say so rather than rendering a form whose save is guaranteed to 409.
  if (!isDraft(competition)) {
    return (
      <FormShell title="Edit competition">
        <Card {...testIdProp(`${PREFIX}-not-editable`)}>
          <CardBody className="flex flex-col items-start gap-12 py-24">
            <p className="text-s14 text-text-secondary">
              This competition is {competitionStatusView(competition.status).label.toLowerCase()},
              so its details can no longer be changed. Only a draft is editable.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() =>
                void navigate({
                  to: paths.competitionDetail,
                  params: { competitionId: competition.id },
                })
              }
              testId={`${PREFIX}-not-editable-back`}
            >
              Back to the competition
            </Button>
          </CardBody>
        </Card>
      </FormShell>
    );
  }

  return <CompetitionForm competition={competition} />;
}

function FormShell({ title, children }: { title: string; children: React.ReactNode }) {
  const navigate = useNavigate();
  const paths = useEmployerModulePaths();
  return (
    <div
      className="mx-auto flex w-full max-w-[720px] flex-col gap-24 px-24 py-24"
      {...testIdProp('page-competitions-form')}
    >
      <PageHeader
        title={title}
        subtitle="The prize is a company-funded donation to the winner’s charity."
        actions={
          <Button
            appearance="saas"
            tone="secondary"
            leadingIcon={<Icon icon={ArrowLeft} size={16} aria-hidden />}
            onClick={() => void navigate({ to: paths.competitions })}
            testId={`${PREFIX}-back`}
          >
            Back
          </Button>
        }
      />
      {children}
    </div>
  );
}

/** A sensible default window: starts tomorrow, runs two weeks. Both are LOCAL-midnight Dates. */
function defaultWindow(): { start: Date; end: Date } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 14);
  return { start, end };
}

function CompetitionForm({ competition }: { competition: AdminStepsCompetitionOut | null }) {
  const navigate = useNavigate();
  const paths = useEmployerModulePaths();
  const queryClient = useQueryClient();
  const isEdit = competition !== null;

  const initialWindow = useMemo(() => {
    const fallback = defaultWindow();
    return {
      start: isoDayToPickedDay(competition?.start_date) ?? fallback.start,
      end: isoDayToPickedDay(competition?.end_date) ?? fallback.end,
    };
  }, [competition?.start_date, competition?.end_date]);

  const [name, setName] = useState(competition?.name ?? '');
  const [description, setDescription] = useState(competition?.description ?? '');
  const [prizeRaw, setPrizeRaw] = useState('');
  const [startDate, setStartDate] = useState<Date>(initialWindow.start);
  const [endDate, setEndDate] = useState<Date>(initialWindow.end);
  const [timezone, setTimezone] = useState(competition?.timezone ?? DEFAULT_TIMEZONE);
  const [visibility, setVisibility] = useState<StepsCompetitionVisibility>(
    competition?.visibility === 'PUBLIC' ? 'PUBLIC' : 'EMPLOYEES_ONLY',
  );

  const [nameError, setNameError] = useState<string | null>(null);
  const [prizeError, setPrizeError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── The Sponsor DPA gate (SC-GO2), create path only ────────────────────────────────────────────
  //
  // ANY non-200 resolves to `null` → the `unreadable` gate, rather than a query error state: a state
  // we could not read is a DESIGNED outcome here, not a failure of the form. (A backend that predates
  // SC-GO2 answers this path with a 422, not a 404 — the URL falls through to `GET /{uuid}` and fails
  // UUID validation — so nothing may key off the status; every non-200 means the same thing.)
  // `retry: false` for the same reason: an automatic retry only delays the honest state, and the
  // `unreadable` gate offers an explicit "Try again".
  const sponsorDpaQuery = useQuery({
    queryKey: SPONSOR_DPA_QUERY_KEY,
    enabled: !isEdit,
    retry: false,
    queryFn: async (): Promise<SponsorDpaState | null> => {
      const { data, error } = await givmoClient.get({ url: SPONSOR_DPA_URL });
      return error === undefined ? extractSponsorDpa(data) : null;
    },
  });

  // The state adopted from a create's 422 refusal, which supersedes the read: the server has just
  // told us what this company is bound to. `undefined` = no refusal yet, so the read governs.
  const [refusedDpa, setRefusedDpa] = useState<SponsorDpaState | null | undefined>(undefined);
  const [dpaAccepted, setDpaAccepted] = useState(false);

  // Held while the read is in flight so a section never flashes in and back out. The gate does NOT
  // block submit while pending — a create then goes up without the field and the server decides.
  const sponsorDpaGate: SponsorDpaGate | null =
    isEdit || (refusedDpa === undefined && sponsorDpaQuery.isPending)
      ? null
      : resolveSponsorDpaGate(
          refusedDpa === undefined ? (sponsorDpaQuery.data ?? null) : refusedDpa,
        );

  const dpaRequired = sponsorDpaGate?.kind === 'required';
  /** The ONLY blocking state: a readable Addendum is being asked for and the box is not ticked. */
  const dpaBlocksSubmit = dpaRequired && !dpaAccepted;

  /** Any newly-resolved gate starts unticked — the admin accepts the text now in front of them. */
  function adoptSponsorDpa(state: SponsorDpaState | null) {
    setRefusedDpa(state);
    setDpaAccepted(false);
  }

  const create = useMutation({
    ...stepsCompetitionsAdminCreateAdminStepsCompetitionMutation(),
    onSuccess: async (result: AdminStepsCompetitionOut) => {
      setSubmitError(null);
      await queryClient.invalidateQueries({
        queryKey: ['steps_competitions_admin-list_admin_steps_competitions'],
      });
      toast.success('Draft competition created.');
      void navigate({
        to: paths.competitionDetail,
        params: { competitionId: result.id },
      });
    },
    onError: (error) => {
      // The Sponsor DPA 422 is a RECOVERY, not a dead end: the refusal carries the current Addendum,
      // so adopt it and render the click-through in place. Reached when the pre-create read was
      // unavailable, or when a new version became effective mid-session. Acceptance is always
      // cleared — the server has just said this company is not bound, so whatever was ticked was
      // ticked against something else, and the admin re-accepts against the text now on screen.
      if (isSponsorDpaAcceptanceRequired(error)) {
        const refused = sponsorDpaFromError(error);
        adoptSponsorDpa(refused);
        setSubmitError(
          resolveSponsorDpaGate(refused).kind === 'required'
            ? SPONSOR_DPA_RECOVERY_COPY
            : errorMessage(error, SPONSOR_DPA_NO_DOCUMENT_COPY),
        );
        return;
      }
      // Surfaces the backend's own copy — including its 422 PRIZE_BELOW_MINIMUM message, which is the
      // authoritative floor even though the client mirrors it above.
      setSubmitError(errorMessage(error, 'We couldn’t create the competition. Please try again.'));
    },
  });

  const update = useMutation({
    ...stepsCompetitionsAdminUpdateAdminStepsCompetitionMutation(),
    onSuccess: async () => {
      setSubmitError(null);
      if (competition === null) return;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: stepsCompetitionsAdminGetAdminStepsCompetitionQueryKey({
            path: { steps_competition_id: competition.id },
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: ['steps_competitions_admin-list_admin_steps_competitions'],
        }),
      ]);
      toast.success('Competition updated.');
      void navigate({
        to: paths.competitionDetail,
        params: { competitionId: competition.id },
      });
    },
    onError: (error) => {
      setSubmitError(errorMessage(error, 'We couldn’t save your changes. Please try again.'));
    },
  });

  const busy = create.isPending || update.isPending;

  function submit() {
    const trimmedName = name.trim();
    setSubmitError(null);

    if (trimmedName.length === 0) {
      setNameError('Give your competition a name.');
      return;
    }
    setNameError(null);

    const start = pickedDayToIsoDay(startDate);
    const end = pickedDayToIsoDay(endDate);
    const rangeError = validateDateRange(start, end);
    if (rangeError !== null) {
      setDateError(rangeError);
      return;
    }
    setDateError(null);

    const trimmedDescription = description.trim();

    if (competition === null) {
      const prize = parsePrizeDollars(prizeRaw);
      if ('error' in prize) {
        setPrizeError(prize.error);
        return;
      }
      setPrizeError(null);
      if (dpaBlocksSubmit) {
        setSubmitError(SPONSOR_DPA_BLOCKED_COPY);
        return;
      }
      // CREATE: omit unset optionals — there is no prior value to clear on a brand-new draft.
      // Built as a typed const rather than a literal at the call site so the additive
      // `sponsor_dpa_accepted` passes structurally ahead of the next snapshot regen.
      const body: CreateCompetitionBody = {
        name: trimmedName,
        ...(trimmedDescription.length > 0 ? { description: trimmedDescription } : {}),
        start_date: start,
        end_date: end,
        prize_amount: prize.value,
        timezone,
        visibility,
        // ONLY when the admin ticked a box against a body they were shown. Never `false`: the key is
        // omitted otherwise, which is what keeps this form working against a pre-SC-GO2 backend and
        // what makes an `unreadable` create ask the server to decide rather than asserting an
        // acceptance the admin never gave.
        ...(dpaRequired && dpaAccepted ? { sponsor_dpa_accepted: true as const } : {}),
      };
      create.mutate({ body });
      return;
    }

    // UPDATE: send the editable fields UNCONDITIONALLY (explicit empty when cleared) so a cleared
    // description actually clears rather than silently keeping the old value while the UI reads as
    // saved. The prize is deliberately absent — it is immutable and the wire body has no field for it.
    update.mutate({
      path: { steps_competition_id: competition.id },
      body: {
        name: trimmedName,
        description: trimmedDescription,
        start_date: start,
        end_date: end,
        timezone,
        visibility,
      },
    });
  }

  return (
    <FormShell title={isEdit ? 'Edit competition' : 'New competition'}>
      <Card>
        <CardHeader
          title="Details"
          subtitle="Name the competition and set the window employees compete over."
        />
        <CardBody className="flex flex-col gap-16">
          <Field
            label="Name"
            value={name}
            maxLength={NAME_MAX_LENGTH}
            onChange={(e) => setName(e.target.value)}
            error={nameError !== null}
            errorMessage={nameError ?? undefined}
            testId={`${PREFIX}-name`}
          />

          <Textarea
            label="Description (optional)"
            value={description}
            rows={3}
            maxLength={DESCRIPTION_MAX_LENGTH}
            onChange={(e) => setDescription(e.target.value)}
            testId={`${PREFIX}-description`}
          />

          <div className="flex flex-wrap items-end gap-12">
            <DatePicker
              label="Starts"
              value={startDate}
              onValueChange={(d) => d && setStartDate(d)}
              className="w-[200px]"
              testId={`${PREFIX}-start-date`}
            />
            <DatePicker
              label="Ends"
              value={endDate}
              onValueChange={(d) => d && setEndDate(d)}
              className="w-[200px]"
              error={dateError !== null}
              errorMessage={dateError ?? undefined}
              testId={`${PREFIX}-end-date`}
            />
          </div>

          <div className="flex flex-wrap items-end gap-12">
            <Select
              appearance="saas"
              label="Time zone"
              options={TIMEZONE_OPTIONS}
              value={timezone}
              onValueChange={(v) => setTimezone(v ?? DEFAULT_TIMEZONE)}
              className="w-[280px]"
              testId={`${PREFIX}-timezone`}
            />
            <Select
              appearance="saas"
              label="Who can see it"
              options={VISIBILITY_OPTIONS}
              value={visibility}
              onValueChange={(v) => setVisibility(v === 'PUBLIC' ? 'PUBLIC' : 'EMPLOYEES_ONLY')}
              className="w-[220px]"
              testId={`${PREFIX}-visibility`}
            />
          </div>
          <p className="text-s12 text-text-tertiary">
            Days are counted in this time zone, so the start and end boundaries land where your team
            expects them.
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Prize"
          subtitle={
            isEdit
              ? 'The prize was set when this competition was created and cannot be changed.'
              : 'Set once, at creation. It cannot be changed later.'
          }
        />
        <CardBody className="flex flex-col gap-12">
          {isEdit ? (
            <p
              className="font-sans text-s20 font-w700 tabular-nums text-text-strong"
              {...testIdProp(`${PREFIX}-prize-readonly`)}
            >
              {formatPrize(competition)}
            </p>
          ) : (
            <Field
              label="Prize amount"
              value={prizeRaw}
              inputMode="decimal"
              leading={<span className="text-text-tertiary">$</span>}
              onChange={(e) => setPrizeRaw(e.target.value)}
              error={prizeError !== null}
              errorMessage={prizeError ?? undefined}
              containerClassName="w-[220px]"
              testId={`${PREFIX}-prize`}
            />
          )}

          {!isEdit && (
            <p
              className={`text-s12 ${
                prizeIsBelowMinimum(prizeRaw) ? 'text-danger-700' : 'text-text-tertiary'
              }`}
              {...testIdProp(`${PREFIX}-prize-hint`)}
            >
              {MIN_PRIZE_HINT}
            </p>
          )}

          <p className="text-s12 text-text-tertiary">
            Publishing holds the prize from your company balance so it is guaranteed.{' '}
            {DEGENERATE_RULE_COPY}
          </p>
        </CardBody>
      </Card>

      {sponsorDpaGate !== null && (
        <SponsorDpaSection
          gate={sponsorDpaGate}
          accepted={dpaAccepted}
          onAcceptedChange={setDpaAccepted}
          onRetry={() => {
            // Drop any adopted refusal so the fresh read governs again, and untick: a re-read can
            // return a different version, and a tick may only ever stand against the text on screen.
            setRefusedDpa(undefined);
            setDpaAccepted(false);
            void sponsorDpaQuery.refetch();
          }}
          retrying={sponsorDpaQuery.isFetching}
          disabled={busy}
        />
      )}

      {submitError !== null && (
        <p className="text-s14 font-w500 text-danger-700" {...testIdProp(`${PREFIX}-submit-error`)}>
          {submitError}
        </p>
      )}

      <div className="flex items-center justify-end gap-8">
        <Button
          appearance="saas"
          tone="secondary"
          onClick={() =>
            isEdit
              ? void navigate({
                  to: paths.competitionDetail,
                  params: { competitionId: competition.id },
                })
              : void navigate({ to: paths.competitions })
          }
          testId={`${PREFIX}-cancel`}
        >
          Cancel
        </Button>
        <Button
          appearance="saas"
          tone="primary"
          loading={busy}
          disabled={dpaBlocksSubmit}
          onClick={submit}
          testId={`${PREFIX}-submit`}
        >
          {isEdit ? 'Save changes' : 'Create draft'}
        </Button>
      </div>
    </FormShell>
  );
}
